import { isApiError } from "@magarac/shared/errors";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import {
  Banner,
  Button,
  Card,
  COPY,
  EmptyState,
  Field,
  Input,
  roleLabel,
  Select,
  Table,
  Td,
  Th,
} from "@magarac/ui";
import { newId } from "@magarac/shared/ids";
import { assignableRoles } from "@magarac/shared/roles";
import { TENANT_ROLES, type TenantRole } from "@magarac/shared/actor";
import {
  inviteMemberResponseSchema,
  memberResponseSchema,
  membersListSchema,
  okSchema,
} from "@magarac/shared/schemas/member";
import { tenantViewSchema } from "@magarac/shared/schemas/tenant";
import { HelpIcon } from "../../components/HelpIcon";
import { api } from "../../lib/api";
import { messageFor } from "../../lib/errors";
import { useTenant } from "../../lib/tenant";

export const Route = createFileRoute("/_app/team")({
  component: TeamPage,
});

/** ACC-3, ACC-5. Screen key: team. */
function TeamPage() {
  const { active } = useTenant();
  const { session } = Route.useRouteContext();
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  // Read once per visit to the page; good enough to tell expired invitations apart.
  const [now] = useState(() => Date.now());
  const queryClient = useQueryClient();
  const tenant = useQuery({
    queryKey: ["tenant", active?.id],
    queryFn: () => api.get("/tenant", tenantViewSchema),
    enabled: Boolean(active),
  });
  const members = useQuery({
    queryKey: ["members", active?.id],
    queryFn: () => api.get("/members", membersListSchema),
    enabled: Boolean(active),
  });
  const myRole = tenant.data?.role;
  const canInvite = myRole === "owner" || myRole === "admin";
  const roles = myRole ? assignableRoles(myRole) : [];

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TenantRole>("member");
  const [inviteKey, setInviteKey] = useState(() => newId());
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // BIL-4: the server asked whether to add a paid seat for this invitation.
  const [seatAsk, setSeatAsk] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["members", active?.id] });

  const invite = useMutation({
    mutationFn: (confirmExtraSeat: boolean) =>
      api.post(
        "/members/invite",
        { email, role, ...(confirmExtraSeat ? { confirmExtraSeat: true } : {}) },
        inviteMemberResponseSchema,
        { idempotencyKey: inviteKey },
      ),
    onSuccess: async (res) => {
      setNotice(
        res.resent
          ? `${email} already had an invitation. We sent it again; it is good for another 7 days.`
          : `Invitation sent to ${email}.`,
      );
      setEmail("");
      setInviteKey(newId());
      setError(null);
      setSeatAsk(null);
      await refresh();
    },
    onError: (err) => {
      setInviteKey(newId());
      if (isApiError(err) && err.code === "seat_limit") {
        setSeatAsk(err.message);
        setError(null);
        return;
      }
      setError(messageFor(err));
    },
  });

  const update = useMutation({
    mutationFn: (input: { id: string; role?: TenantRole; status?: "active" | "deactivated" }) =>
      api.patch(
        `/members/${input.id}`,
        { role: input.role, status: input.status },
        memberResponseSchema,
      ),
    onSuccess: refresh,
    onError: (err) => setError(messageFor(err)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/members/${id}`, okSchema),
    onSuccess: async () => {
      setConfirmRemove(null);
      await refresh();
    },
    onError: (err) => setError(messageFor(err)),
  });

  const resend = useMutation({
    mutationFn: (id: string) =>
      api.post(`/members/invitations/${id}/resend`, {}, inviteMemberResponseSchema),
    onSuccess: async (res) => {
      setError(null);
      setNotice(`Invitation sent again to ${res.invitation.email}.`);
      await refresh();
    },
    onError: (err) => setError(messageFor(err)),
  });

  const cancelInvite = useMutation({
    mutationFn: (id: string) => api.delete(`/members/invitations/${id}`, okSchema),
    onSuccess: async () => {
      setNotice("Invitation canceled. The link in that email no longer works.");
      await refresh();
    },
    onError: (err) => setError(messageFor(err)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setSeatAsk(null);
    invite.mutate(false);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{COPY.team}</h1>
        <HelpIcon screen="team" />
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {notice ? <Banner tone="ok">{notice}</Banner> : null}

      {canInvite ? (
        <Card title="Invite someone" className="mb-6">
          <form
            onSubmit={submit}
            noValidate
            className="grid gap-2 md:grid-cols-[1fr_180px_auto] md:items-end"
          >
            <Field id="invite-email" label="Email">
              <Input
                id="invite-email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
            <Field id="invite-role" label="Role" hint={COPY.roleHelp[role]}>
              <Select
                id="invite-role"
                value={role}
                onChange={(e) => setRole(e.target.value as TenantRole)}
              >
                {TENANT_ROLES.filter((r) => roles.includes(r)).map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="mb-4">
              <Button type="submit" busy={invite.isPending} disabled={!email}>
                Send invitation
              </Button>
            </div>
          </form>
          {seatAsk ? (
            <div className="mt-2 rounded-lg border border-line bg-canvas p-3">
              <p className="mb-2">{seatAsk}</p>
              {myRole === "owner" ? (
                <div className="flex flex-wrap gap-2">
                  <Button busy={invite.isPending} onClick={() => invite.mutate(true)}>
                    Add a seat and invite
                  </Button>
                  <Button variant="secondary" onClick={() => setSeatAsk(null)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted">
                  Only the owner can add a paid seat. Ask them to send this invitation.
                </p>
              )}
            </div>
          ) : null}
        </Card>
      ) : null}

      <Card title={COPY.teamMembers}>
        {members.data && members.data.members.length === 0 ? (
          <EmptyState title={`No ${COPY.teamMembers} yet`} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                {canInvite ? <Th>Actions</Th> : null}
              </tr>
            </thead>
            <tbody>
              {members.data?.members.map((m) => (
                <tr key={m.id}>
                  <Td>
                    <div className="font-medium">{m.name ?? m.email}</div>
                    <div className="text-xs text-muted">{m.email}</div>
                  </Td>
                  <Td>
                    {canInvite && roles.includes(m.role) ? (
                      <Select
                        aria-label={`Role for ${m.name ?? m.email}`}
                        value={m.role}
                        onChange={(e) =>
                          update.mutate({ id: m.id, role: e.target.value as TenantRole })
                        }
                      >
                        {roles.map((r) => (
                          <option key={r} value={r}>
                            {roleLabel(r)}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      roleLabel(m.role)
                    )}
                  </Td>
                  <Td>{m.status === "active" ? "Active" : "Deactivated"}</Td>
                  {canInvite && m.userId === session.user.id ? (
                    <Td className="text-sm text-muted">You</Td>
                  ) : canInvite && confirmRemove === m.id ? (
                    <Td>
                      <p className="mb-2 text-sm">
                        Remove {m.name ?? m.email}? They lose access right away. Their past work
                        stays in the history.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="danger"
                          busy={remove.isPending}
                          onClick={() => remove.mutate(m.id)}
                        >
                          Yes, remove
                        </Button>
                        <Button variant="secondary" onClick={() => setConfirmRemove(null)}>
                          Keep
                        </Button>
                      </div>
                    </Td>
                  ) : canInvite ? (
                    <Td>
                      <div className="flex flex-wrap gap-2">
                        {m.status === "active" ? (
                          <Button
                            variant="secondary"
                            onClick={() => update.mutate({ id: m.id, status: "deactivated" })}
                          >
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            variant="secondary"
                            onClick={() => update.mutate({ id: m.id, status: "active" })}
                          >
                            Reactivate
                          </Button>
                        )}
                        {myRole === "owner" ? (
                          <Button variant="danger" onClick={() => setConfirmRemove(m.id)}>
                            Remove
                          </Button>
                        ) : null}
                      </div>
                    </Td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {members.data && members.data.invitations.length > 0 ? (
        <Card title="Pending invitations" className="mt-6">
          <ul>
            {members.data.invitations.map((i) => (
              <li
                key={i.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-line py-2 last:border-0"
              >
                <span>
                  {i.email} <span className="text-muted">({roleLabel(i.role)})</span>
                  <span className="block text-sm text-muted">
                    {new Date(i.expiresAt).getTime() <= now
                      ? "Expired. Send it again to give them another 7 days."
                      : `Expires ${new Date(i.expiresAt).toLocaleDateString()}`}
                  </span>
                </span>
                {canInvite ? (
                  <span className="flex gap-2">
                    <Button
                      variant="secondary"
                      busy={resend.isPending && resend.variables === i.id}
                      onClick={() => resend.mutate(i.id)}
                    >
                      Send again
                    </Button>
                    <Button
                      variant="ghost"
                      busy={cancelInvite.isPending && cancelInvite.variables === i.id}
                      onClick={() => cancelInvite.mutate(i.id)}
                    >
                      Cancel invitation
                    </Button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}
